import React from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import DiscussionPage from './pages/DiscussionPage';
import TopicPage from './pages/TopicPage';
import EventsPage from './pages/EventsPage';
import EventPage from './pages/EventPage';
import ConferencePage from './pages/ConferencePage';
import ConferenceSessionDiscussionPage from './pages/ConferenceSessionDiscussionPage';
import FeedbackFormPage from './pages/FeedbackFormPage';
import QrLanderPage from './pages/QrLanderPage';
import DirectoryPage from './pages/DirectoryPage';
import NotificationsPage from './pages/NotificationsPage';
import HelpPage from './pages/HelpPage';
import SearchPage from './pages/SearchPage';
import AdminPage from './pages/admin/AdminPage';
import AdminAccessGate from './pages/admin/AdminAccessGate';
import AdminPrizesPage from './pages/admin/AdminPrizesPage';
import TagsAdminPage from './pages/admin/TagsAdminPage';
import PollPage, { PollRespondPage } from './pages/PollPage';
import PublicProfilePage from './pages/PublicProfilePage';
import { AuthProvider } from './auth/AuthProvider';
import { AuthGate } from './auth/AuthGate';
import { trackPageView } from './lib/analytics';
import { subscribeForegroundPushMessages, showForegroundBrowserNotification } from './lib/browserPush';
import { useAuth } from './auth/AuthProvider';

function AppShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function AnalyticsRouteTracker() {
  const location = useLocation();
  const lastPathRef = React.useRef<string>('');

  React.useEffect(() => {
    const nextPath = `${location.pathname}${location.search}${location.hash}`;
    if (lastPathRef.current === nextPath) return;
    lastPathRef.current = nextPath;
    void trackPageView(nextPath);
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function ForegroundPushListener() {
  const { user } = useAuth();

  React.useEffect(() => {
    if (!user?.sub) return;

    let active = true;
    let unsubscribe: () => void = () => undefined;

    void subscribeForegroundPushMessages((message) => {
      if (!active) return;
      showForegroundBrowserNotification(message);
    }).then((next) => {
      unsubscribe = next;
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [user?.sub]);

  return null;
}

function AppRoutes() {
  return (
    <>
      <AnalyticsRouteTracker />
      <ForegroundPushListener />
      <Routes>
        <Route path="/qrlander" element={<QrLanderPage />} />
        <Route path="/poll/p/:pollId" element={<PollRespondPage />} />
        <Route element={<AppShellLayout />}>
          <Route path="/" element={<Navigate to="/discussions" replace />} />
          <Route path="/discussion" element={<Navigate to="/discussions" replace />} />
          <Route path="/discussions" element={<DiscussionPage />} />
          <Route path="/discussion/:topicId" element={<TopicPage />} />
          <Route path="/poll" element={<PollPage />} />
          <Route path="/poll/host" element={<PollPage />} />
          <Route path="/poll/host/:pollId" element={<PollPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:eventId" element={<EventPage />} />
          <Route path="/conference" element={<ConferencePage />} />
          <Route path="/conference/:sessionId" element={<ConferenceSessionDiscussionPage />} />
          <Route path="/feedback/:formId" element={<FeedbackFormPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/profile/:profileId" element={<PublicProfilePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route
            path="/admin/tags"
            element={
              <AdminAccessGate>
                <TagsAdminPage />
              </AdminAccessGate>
            }
          />
          <Route
            path="/admin/prizes"
            element={
              <AdminAccessGate>
                <AdminPrizesPage />
              </AdminAccessGate>
            }
          />
          <Route path="*" element={<Navigate to="/discussions" replace />} />
        </Route>
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AuthGate>
        <AppRoutes />
      </AuthGate>
    </AuthProvider>
  );
}
